import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MainLayout from "../layout/MainLayout";
import { useAlertContext } from "../context/AlertContext";
import OrgUnitPanel from "../components/orgchart/OrgUnitPanel";
import {
    addOrgLink, addOrgMember, createOrgUnit, deleteOrgUnit, getOrgChart, getOrgChartLog, importOrgDepartments,
    removeOrgLink, removeOrgMember, reorderOrgUnits, resetOrgPositions, restoreOrgUnit, setOrgUnitPosition,
    updateOrgMember, updateOrgUnit,
} from "../services/orgChartApi";
import { getTablePrefs, saveTablePrefs } from "../services/uiPrefsApi";
import {
    CARD_W, MAX_HEADS_ON_CARD, MAX_MEMBERS_ON_CARD, ancestorIds, applyOffsets, buildTree, canMoveUnit, cardHeight,
    childIds, edgePath, formatField, hitTest, initials, layoutTree, leadersOf, linkPath, membersByUnit, peopleCount,
    plural, searchUnits, shortName, staffOf, subtreeIds,
} from "../utils/orgChart";

/**
 * «Структура компании» — управленческая структура как в Битрикс24 (правка №9, Чат 2).
 * [Claude] 2026-11-02. Дерево на холсте (масштаб, перетаскивание холста, «Найти меня»,
 * поиск, свернуть/развернуть), вид списком, панель подразделения, личный выбор данных
 * сотрудника на карточках. Менять — только с правом «Структура компании: менять».
 *
 * Доработка 03.11.2026 (Чат 4, по просьбе Светы):
 * - карточку тащим мышью: на другую карточку — подчинить ей, на пустое место — просто сдвинуть
 *   (ветка едет вместе, расположение общее для всех); зоны «на верхний уровень» и «удалить»;
 * - раскладка сверху вниз или слева направо (у каждого своя);
 * - дополнительное подчинение — пунктир (подразделение подчиняется нескольким);
 * - список сотрудников на карточке, ЧС/ЧМ (числа судьбы) среди данных;
 * - «Скрыть у меня» — подразделение не показывается только этому пользователю.
 */

const PREFS_KEY = "org_chart";
const DEFAULT_PREFS = { fields: ["position"], view: "tree", collapsed: [], hidden: [], direction: "v", showStaff: true };
const ZOOM_MIN = 0.3;
const ZOOM_MAX = 1.6;
const DRAG_THRESHOLD = 5;
const SNAP = 4;
const snap = (v) => Math.round(v / SNAP) * SNAP;

function UnitModal({ units, parentId, title = "Новое подразделение", onClose, onCreate }) {
    const [name, setName] = useState("");
    const [parent, setParent] = useState(parentId ?? "");
    const submit = (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        onCreate({ name: name.trim(), parent_id: parent === "" ? null : Number(parent) });
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" role="presentation">
            <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" role="dialog" aria-label={title}>
                <h3 className="text-lg font-bold text-slate-900 mb-3">{title}</h3>
                <label className="block text-xs text-slate-600 mb-2">
                    Название
                    <input autoFocus value={name} onChange={(e) => setName(e.target.value)} aria-label="Название нового подразделения"
                        className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                </label>
                <label className="block text-xs text-slate-600 mb-4">
                    Входит в
                    <select value={parent} onChange={(e) => setParent(e.target.value)} aria-label="Входит в"
                        className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                        <option value="">— верхний уровень —</option>
                        {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                </label>
                <div className="flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-slate-300">Отмена</button>
                    <button type="submit" disabled={!name.trim()} className="px-4 py-2 text-sm rounded-lg bg-[#e0bb48] font-semibold disabled:opacity-50">Создать</button>
                </div>
            </form>
        </div>
    );
}

/** Подтверждение удаления: заранее говорим, куда уйдут подотделы и люди (как в Битрикс24). */
function DeleteModal({ target, onClose, onConfirm }) {
    const { name, parentName, sub, people, archived } = target;
    return (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" role="presentation" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" role="dialog" aria-label="Удалить подразделение" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Удалить «{name}»?</h3>
                <div className="text-sm text-slate-600 space-y-1.5 mb-4" data-testid="org-delete-text">
                    {archived ? <p>Подразделение будет удалено из архива навсегда.</p> : parentName ? (
                        <>
                            <p>Подотделы ({sub}) и сотрудники ({people}) перейдут в «{parentName}».</p>
                            {people > 0 && <p className="text-xs text-slate-500">Руководители удалённого подразделения станут там обычными сотрудниками.</p>}
                        </>
                    ) : (
                        <>
                            <p>Подотделы ({sub}) станут подразделениями верхнего уровня.</p>
                            {people > 0 && <p>Сотрудники ({people}) будут убраны из структуры — их карточки сохранятся.</p>}
                        </>
                    )}
                    <p className="text-xs text-slate-400">Изменение попадёт в журнал структуры.</p>
                </div>
                <div className="flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-slate-300">Отмена</button>
                    <button type="button" onClick={onConfirm} className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white font-semibold hover:bg-red-500">Удалить</button>
                </div>
            </div>
        </div>
    );
}

/** «Подчинить другому…»: поиск по всем подразделениям, куда можно перенести (без своих подотделов). */
function MoveModal({ unit, options, onClose, onMove }) {
    const [q, setQ] = useState("");
    const nq = q.trim().toLowerCase().replace(/ё/g, "е");
    const shown = options.filter((o) => !nq || o.path.toLowerCase().replace(/ё/g, "е").includes(nq));
    return (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" role="presentation" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-5 flex flex-col max-h-[80vh]" role="dialog" aria-label="Подчинить другому подразделению" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-slate-900">Кому подчиняется «{unit.name}»</h3>
                <p className="text-xs text-slate-500 mb-2">Подразделение переедет вместе со всеми подотделами и сотрудниками.</p>
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Найти подразделение"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-2" aria-label="Найти подразделение" />
                <div className="flex-1 overflow-y-auto border border-slate-100 rounded-lg">
                    {unit.parent_id != null && (
                        <button type="button" onClick={() => onMove(null)} className="block w-full text-left px-3 py-2 text-sm hover:bg-sky-50 border-b border-slate-100 font-semibold text-slate-700">
                            ↑ Верхний уровень
                        </button>
                    )}
                    {shown.map((o) => (
                        <button key={o.id} type="button" onClick={() => onMove(o.id)} className="block w-full text-left px-3 py-2 text-sm hover:bg-sky-50 border-b border-slate-100 last:border-0">
                            <span className="font-semibold text-slate-800">{o.name}</span>
                            {o.parents && <span className="block text-[11px] text-slate-400">{o.parents}</span>}
                        </button>
                    ))}
                    {!shown.length && <p className="px-3 py-3 text-sm text-slate-400">Ничего не найдено</p>}
                </div>
                <div className="flex justify-end mt-3">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-slate-300">Отмена</button>
                </div>
            </div>
        </div>
    );
}

function Drawer({ title, onClose, children }) {
    return (
        <div className="absolute top-0 right-0 bottom-0 w-[380px] bg-white border-l border-slate-200 shadow-xl z-30 flex flex-col" role="dialog" aria-label={title}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <h3 className="font-bold text-slate-900">{title}</h3>
                <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 text-lg" aria-label="Закрыть">×</button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-2 text-sm">{children}</div>
        </div>
    );
}

/** Карточка подразделения в стиле Битрикс24: руководители, список подчинённых, подотделы внизу. */
function OrgCard({
    node, unit, leaders, staff, employeesById, fieldLines, fields, roles, people, kids, collapsed, mine, selected, matched,
    canEdit, dropOk, dragging, extraParents, showStaff, direction, onSelect, onToggle, onAddChild, onMenu, onMouseDown,
}) {
    const shown = leaders.slice(0, MAX_HEADS_ON_CARD);
    const staffShown = staff.slice(0, MAX_MEMBERS_ON_CARD);
    const withNumerology = fields.includes("numerology");
    const ring = dropOk ? "ring-4 ring-emerald-300 border-emerald-400" : selected ? "ring-2 ring-sky-400" : matched ? "ring-2 ring-[#e0bb48]" : "";
    return (
        <div
            className={`absolute bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md flex flex-col select-none ${ring} ${dragging ? "pointer-events-none opacity-90 shadow-xl z-20" : "transition-shadow"} ${canEdit ? "cursor-grab active:cursor-grabbing" : ""}`}
            style={{ left: node.x, top: node.y, width: CARD_W, height: node.h }}
            data-testid="org-card"
            onMouseDown={onMouseDown}
            title={canEdit ? "Тащите карточку: на другое подразделение — подчинить ему, на пустое место — сдвинуть" : undefined}
        >
            {dropOk && (
                <span className="absolute -top-2.5 left-3 text-[10px] font-semibold bg-emerald-500 text-white rounded-full px-2 py-0.5 z-10">подчинить сюда</span>
            )}
            {mine && (
                <span className="absolute -top-2 right-3 text-[10px] font-semibold bg-sky-500 text-white rounded-full px-2 py-0.5">ваш отдел</span>
            )}
            <div className="h-[40px] flex items-center gap-1 pl-3 pr-1 border-b border-slate-100">
                <button type="button" onClick={onSelect} className="flex-1 min-w-0 text-left text-[14px] font-bold text-slate-800 truncate" title={unit.name}>
                    {unit.name}
                </button>
                {extraParents.length > 0 && (
                    <span className="shrink-0 text-[10px] font-semibold text-violet-700 bg-violet-50 rounded-full px-1.5 py-0.5"
                        title={`Также подчиняется: ${extraParents.join(", ")}`} data-testid="org-extra-parents">
                        ⇢ {extraParents.length}
                    </span>
                )}
                <button type="button" data-nodrag="1" onClick={onMenu} className="shrink-0 text-slate-400 hover:text-slate-700 px-1.5 text-lg leading-none" aria-label={`Действия: ${unit.name}`}>⋯</button>
            </div>
            <button type="button" onClick={onSelect} className="flex-1 text-left px-3 overflow-hidden">
                {shown.length ? shown.map((m) => {
                    const emp = employeesById.get(m.employee_id);
                    const sub = fields.includes("position") ? (m.title || emp?.position) : null;
                    const lines = fields.filter((f) => f !== "position").map((f) => formatField(f, emp));
                    return (
                        <div key={m.id} className="flex gap-2 pt-2" style={{ height: 36 + 14 * fieldLines }}>
                            <span className="w-7 h-7 shrink-0 rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold flex items-center justify-center">
                                {initials(emp?.full_name)}
                            </span>
                            <div className="min-w-0 leading-[14px]">
                                <div className="text-[12.5px] font-semibold text-slate-800 truncate">
                                    {emp?.full_name}
                                    {m.role === "deputy" && <span className="ml-1 text-[10px] text-sky-700 font-normal">{roles.deputy?.toLowerCase()}</span>}
                                </div>
                                <div className="text-[11px] text-slate-500 truncate">{sub || " "}</div>
                                {lines.map((l, i) => <div key={fields[i] + String(i)} className="text-[11px] text-slate-500 truncate">{l || " "}</div>)}
                            </div>
                        </div>
                    );
                }) : <div className="h-[24px] pt-1.5 text-[11px] text-slate-400">Руководитель не назначен</div>}
                {leaders.length > MAX_HEADS_ON_CARD && (
                    <div className="h-4 text-[10px] text-slate-400">и ещё {leaders.length - MAX_HEADS_ON_CARD}</div>
                )}
                {showStaff && (
                    <div className="pb-1" data-testid="org-card-staff">
                        <div className="h-[22px] pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                            Подчинённые · {staff.length}
                        </div>
                        {staffShown.length ? staffShown.map((m) => {
                            const emp = employeesById.get(m.employee_id);
                            const pos = fields.includes("position") ? (m.title || emp?.position) : null;
                            const num = withNumerology ? formatField("numerology", emp) : null;
                            return (
                                <div key={m.id} className="h-[18px] text-[11.5px] text-slate-700 truncate">
                                    <span className="text-slate-300">•</span> {shortName(emp?.full_name)}
                                    {pos && <span className="text-slate-400"> · {pos}</span>}
                                    {num && <span className="text-violet-600"> · {num}</span>}
                                </div>
                            );
                        }) : <div className="h-[18px] text-[11px] text-slate-400">нет сотрудников</div>}
                        {staff.length > MAX_MEMBERS_ON_CARD && (
                            <div className="h-4 text-[10px] text-sky-700">и ещё {staff.length - MAX_MEMBERS_ON_CARD}</div>
                        )}
                    </div>
                )}
            </button>
            {kids > 0 ? (
                <button type="button" data-nodrag="1" onClick={onToggle}
                    className={`h-[32px] shrink-0 border-t border-slate-100 rounded-b-xl text-[12px] font-semibold flex items-center justify-center gap-1 ${collapsed ? "text-slate-600 hover:bg-slate-50" : "bg-sky-50 text-sky-700 hover:bg-sky-100"}`}
                    aria-label={`${collapsed ? "Развернуть" : "Свернуть"} ${unit.name}`}
                    title={`Всего людей с подотделами: ${people}`}>
                    {kids} {plural(kids, "отдел", "отдела", "отделов")} {collapsed ? "⌄" : "⌃"}
                </button>
            ) : (
                <div className="h-[32px] shrink-0 border-t border-slate-100 text-[11px] text-slate-400 flex items-center justify-center">нет отделов в подчинении</div>
            )}
            {canEdit && (
                <button
                    type="button"
                    data-nodrag="1"
                    onClick={onAddChild}
                    className={`absolute w-6 h-6 rounded-full bg-white border border-sky-300 text-sky-600 text-sm leading-none hover:bg-sky-50 ${direction === "h" ? "top-1/2 -translate-y-1/2 -right-3" : "left-1/2 -translate-x-1/2 -bottom-3"}`}
                    aria-label={`Добавить подотдел в ${unit.name}`}
                >
                    +
                </button>
            )}
        </div>
    );
}

export default function OrgChartPage() {
    const { showAlert } = useAlertContext();
    const [chart, setChart] = useState(null);
    const [error, setError] = useState("");
    const [prefs, setPrefs] = useState(DEFAULT_PREFS);
    const [selectedId, setSelectedId] = useState(null);
    const [zoom, setZoom] = useState(0.85);
    const [pan, setPan] = useState({ x: 16, y: 24 });
    const [query, setQuery] = useState("");
    const [hitIndex, setHitIndex] = useState(0);
    const [modal, setModal] = useState(null); // { parentId, above? }
    const [menu, setMenu] = useState(null); // { unitId, x, y }
    const [drawer, setDrawer] = useState(null); // "fields" | "log" | "archive" | "hidden"
    const [log, setLog] = useState([]);
    const [archived, setArchived] = useState([]);
    const [dragLive, setDragLive] = useState(null); // { id, dx, dy } — сдвиг в единицах холста во время перетаскивания
    const [dropId, setDropId] = useState(null);
    const [dropZone, setDropZone] = useState(null); // "top" | "delete"
    const [deleteFor, setDeleteFor] = useState(null);
    const [moveFor, setMoveFor] = useState(null);
    const viewportRef = useRef(null);
    const panning = useRef(null);
    const dragging = useRef(null); // { id, sx, sy, moved }
    const justDragged = useRef(false);
    const live = useRef({});
    const fitted = useRef(false);
    const saveTimer = useRef(null);

    const load = useCallback(async () => {
        try {
            const data = await getOrgChart();
            setChart(data);
            setError("");
        } catch (e) {
            setError(e?.message || "Не удалось загрузить структуру");
        }
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        let alive = true;
        Promise.resolve(getTablePrefs(PREFS_KEY))
            .then((p) => { if (alive && p && typeof p === "object") setPrefs({ ...DEFAULT_PREFS, ...p }); })
            .catch(() => {});
        return () => { alive = false; };
    }, []);
    useEffect(() => () => clearTimeout(saveTimer.current), []);

    const updatePrefs = useCallback((patch) => {
        setPrefs((prev) => {
            const next = { ...prev, ...patch };
            clearTimeout(saveTimer.current);
            saveTimer.current = setTimeout(() => { Promise.resolve(saveTablePrefs(PREFS_KEY, next)).catch(() => {}); }, 400);
            return next;
        });
    }, []);

    const units = useMemo(() => chart?.units || [], [chart]);
    const tree = useMemo(() => buildTree(units), [units]);
    const byUnit = useMemo(() => membersByUnit(chart?.members || []), [chart]);
    const employeesById = useMemo(() => new Map((chart?.employees || []).map((e) => [e.id, e])), [chart]);
    const roles = chart?.roles || { head: "Руководитель", deputy: "Заместитель", member: "Сотрудник" };
    const canEdit = Boolean(chart?.can_edit);
    const meId = chart?.me?.employee_id ?? null;
    const allowedFields = useMemo(() => new Set((chart?.fields || []).filter((f) => f.allowed).map((f) => f.key)), [chart]);
    const fields = useMemo(() => (prefs.fields || []).filter((f) => !chart || allowedFields.has(f)), [prefs.fields, allowedFields, chart]);
    const fieldLines = fields.filter((f) => f !== "position").length;
    const collapsed = useMemo(() => new Set(prefs.collapsed || []), [prefs.collapsed]);
    const hidden = useMemo(() => new Set((prefs.hidden || []).filter((id) => tree.byId.has(id))), [prefs.hidden, tree]);
    const direction = prefs.direction === "h" ? "h" : "v";
    const showStaff = prefs.showStaff !== false;
    const myUnits = useMemo(() => new Set((chart?.members || []).filter((m) => meId != null && m.employee_id === meId).map((m) => m.unit_id)), [chart, meId]);
    const links = useMemo(() => (chart?.links || []).filter((l) => tree.byId.has(l.unit_id) && tree.byId.has(l.parent_id)), [chart, tree]);
    const offsets = useMemo(() => new Map((chart?.positions || []).filter((p) => p.mode === direction).map((p) => [p.unit_id, { dx: p.dx, dy: p.dy }])), [chart, direction]);

    const heightOf = useCallback(
        (id) => cardHeight(leadersOf(byUnit, id).length, fieldLines, showStaff ? staffOf(byUnit, id).length : -1),
        [byUnit, fieldLines, showStaff],
    );
    const placeTree = useCallback((foldedSet, extra = null) => applyOffsets(
        layoutTree(tree, { collapsed: foldedSet, hidden, heightOf, direction }), tree, offsets, extra,
    ), [tree, hidden, heightOf, direction, offsets]);
    const layout = useMemo(() => placeTree(collapsed, dragLive), [placeTree, collapsed, dragLive]);
    const nodeById = useMemo(() => new Map(layout.nodes.map((n) => [n.id, n])), [layout]);

    const hits = useMemo(() => searchUnits(query, units, chart?.members || [], employeesById), [query, units, chart, employeesById]);
    const hitSet = useMemo(() => new Set(hits), [hits]);

    const centerOn = useCallback((id, z = zoom) => {
        const vp = viewportRef.current;
        const n = placeTree(new Set([...collapsed].filter((c) => !ancestorIds(tree, id).includes(c)))).nodes.find((x) => x.id === id);
        if (!n) return;
        const w = vp?.clientWidth || 1000;
        const h = vp?.clientHeight || 600;
        setPan({ x: w / 2 - (n.x + n.w / 2) * z, y: h / 3 - n.y * z });
    }, [tree, collapsed, placeTree, zoom]);

    const reveal = useCallback((id) => {
        const anc = ancestorIds(tree, id);
        const patch = {};
        if (anc.some((a) => collapsed.has(a))) patch.collapsed = [...collapsed].filter((c) => !anc.includes(c));
        if ([...anc, id].some((a) => hidden.has(a))) patch.hidden = [...hidden].filter((h) => ![...anc, id].includes(h));
        if (Object.keys(patch).length) updatePrefs(patch);
        setSelectedId(id);
        centerOn(id);
    }, [tree, collapsed, hidden, updatePrefs, centerOn]);

    // Fit the whole tree into the width (first load and the «показать всё» button).
    const fitAll = useCallback(() => {
        const vw = viewportRef.current?.clientWidth || 0;
        if (!vw || !layout.nodes.length) return;
        const fullW = Math.max(layout.width - (layout.minX || 0), 1);
        const z = Math.max(ZOOM_MIN, Math.min(1, (vw - 32) / fullW));
        setZoom(Number(z.toFixed(2)));
        setPan({ x: Math.max(16, (vw - fullW * z) / 2) - (layout.minX || 0) * z, y: 24 - (layout.minY || 0) * z });
    }, [layout]);

    useEffect(() => {
        if (fitted.current || !layout.nodes.length || !viewportRef.current) return;
        fitted.current = true;
        fitAll();
    }, [layout, fitAll, prefs.view]);

    // Ctrl + колесо — масштаб, колесо — сдвиг холста.
    useEffect(() => {
        const vp = viewportRef.current;
        if (!vp) return undefined;
        const onWheel = (e) => {
            e.preventDefault();
            if (e.ctrlKey || e.metaKey) {
                setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Number((z - e.deltaY * 0.0015).toFixed(2)))));
            } else {
                setPan((p) => ({ x: p.x - (e.shiftKey ? e.deltaY : e.deltaX), y: p.y - (e.shiftKey ? 0 : e.deltaY) }));
            }
        };
        vp.addEventListener("wheel", onWheel, { passive: false });
        return () => vp.removeEventListener("wheel", onWheel);
    }, [chart, prefs.view]);

    const act = async (fn, ok) => {
        try {
            await fn();
            if (ok) showAlert(ok, "success");
            await load();
        } catch (e) {
            showAlert(e?.message || "Не удалось сохранить", "error");
        }
    };

    const moveUnit = (unitId, parentId) => {
        if (!canMoveUnit(tree, unitId, parentId)) {
            showAlert("Нельзя перенести подразделение внутрь самого себя или своего подотдела", "error");
            return;
        }
        const hadOffset = offsets.has(unitId);
        act(async () => {
            await updateOrgUnit(unitId, { parent_id: parentId });
            // На новом месте карточка встаёт автоматически — старый ручной сдвиг там ни к чему.
            if (hadOffset) await setOrgUnitPosition(unitId, { mode: direction, dx: 0, dy: 0 });
        }, "Подразделение перенесено");
    };

    // Сдвиг на пустое место: сохраняем сразу у себя и на сервере (видят все).
    const nudge = (unitId, dx, dy) => {
        const base = offsets.get(unitId) || { dx: 0, dy: 0 };
        const next = { unit_id: unitId, mode: direction, dx: snap(base.dx + dx), dy: snap(base.dy + dy) };
        if (next.dx === base.dx && next.dy === base.dy) return;
        setChart((c) => (c ? {
            ...c,
            positions: [...(c.positions || []).filter((p) => !(p.unit_id === unitId && p.mode === direction)), next],
        } : c));
        Promise.resolve(setOrgUnitPosition(unitId, { mode: direction, dx: next.dx, dy: next.dy }))
            .catch((e) => { showAlert(e?.message || "Не удалось сохранить расположение", "error"); load(); });
    };

    // Свежие значения для обработчиков мыши на window.
    live.current = { zoom, pan, layout, tree, canEdit };
    const finishDrag = useRef(() => {});

    useEffect(() => {
        const toCanvas = (e) => {
            const { zoom: z, pan: p } = live.current;
            const rect = viewportRef.current?.getBoundingClientRect?.() || { left: 0, top: 0 };
            return { x: (e.clientX - rect.left - p.x) / z, y: (e.clientY - rect.top - p.y) / z };
        };
        const zoneAt = (e) => {
            if (typeof document.elementFromPoint !== "function") return null;
            return document.elementFromPoint(e.clientX, e.clientY)?.closest?.("[data-dropzone]")?.getAttribute("data-dropzone") || null;
        };
        const move = (e) => {
            if (panning.current) {
                const { sx, sy, px, py } = panning.current;
                setPan({ x: px + e.clientX - sx, y: py + e.clientY - sy });
                return;
            }
            const d = dragging.current;
            if (!d) return;
            const ddx = e.clientX - d.sx;
            const ddy = e.clientY - d.sy;
            if (!d.moved && Math.hypot(ddx, ddy) < DRAG_THRESHOLD) return;
            d.moved = true;
            const { zoom: z, layout: l, tree: t } = live.current;
            setDragLive({ id: d.id, dx: ddx / z, dy: ddy / z });
            const pt = toCanvas(e);
            const target = hitTest(l.nodes, pt.x, pt.y, new Set(subtreeIds(t, d.id)));
            setDropId(target != null && target !== t.byId.get(d.id)?.parent_id && canMoveUnit(t, d.id, target) ? target : null);
            setDropZone(zoneAt(e));
        };
        const up = (e) => {
            panning.current = null;
            const d = dragging.current;
            dragging.current = null;
            if (!d) return;
            if (d.moved) {
                justDragged.current = true;
                setTimeout(() => { justDragged.current = false; }, 0);
                const { zoom: z, layout: l, tree: t } = live.current;
                const pt = toCanvas(e);
                const target = hitTest(l.nodes, pt.x, pt.y, new Set(subtreeIds(t, d.id)));
                finishDrag.current(d.id, (e.clientX - d.sx) / z, (e.clientY - d.sy) / z, target, zoneAt(e));
            }
            setDragLive(null);
            setDropId(null);
            setDropZone(null);
        };
        window.addEventListener("mousemove", move);
        window.addEventListener("mouseup", up);
        return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    }, []);

    const startCardDrag = (e, id) => {
        if (e.button !== 0) return;
        e.stopPropagation();
        setMenu(null);
        if (!canEdit || e.target.closest?.("[data-nodrag]")) return;
        dragging.current = { id, sx: e.clientX, sy: e.clientY, moved: false };
    };
    const guardClick = (fn) => () => { if (!justDragged.current) fn(); };

    const toggle = (id) => {
        const next = new Set(collapsed);
        if (next.has(id)) next.delete(id); else next.add(id);
        updatePrefs({ collapsed: [...next] });
    };
    const foldAll = (fold) => updatePrefs({ collapsed: fold ? units.filter((u) => childIds(tree, u.id).length).map((u) => u.id) : [] });
    const hideUnit = (id) => {
        updatePrefs({ hidden: [...new Set([...hidden, id])] });
        if (selectedId != null && subtreeIds(tree, id).includes(selectedId)) setSelectedId(null);
    };

    const findMe = () => {
        const first = units.find((u) => myUnits.has(u.id));
        if (first) reveal(first.id);
        else showAlert("Вас пока нет в структуре", "info");
    };

    // Enter / ↓ — show the next match (first press — the first one).
    const gotoHit = () => {
        if (!hits.length) return;
        const i = hitIndex % hits.length;
        reveal(hits[i]);
        setHitIndex(i + 1);
    };

    const shift = (unitId, dir) => {
        const u = tree.byId.get(unitId);
        const siblings = u?.parent_id != null && tree.byId.has(u.parent_id) ? childIds(tree, u.parent_id) : tree.roots;
        const i = siblings.indexOf(unitId);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= siblings.length) return;
        const next = [...siblings];
        [next[i], next[j]] = [next[j], next[i]];
        act(() => reorderOrgUnits(u.parent_id ?? null, next));
    };

    // Куда можно подчинить подразделение: все, кроме него самого и его подотделов; с путём для понятности.
    const parentOptions = (unitId) => {
        const out = [];
        const walkAll = (id) => {
            if (id !== unitId && canMoveUnit(tree, unitId, id)) {
                const path = ancestorIds(tree, id).map((a) => tree.byId.get(a)?.name).filter(Boolean);
                const name = tree.byId.get(id)?.name || "";
                out.push({ id, name, parents: path.join(" / "), path: [...path, name].join(" / "), depth: path.length });
            }
            childIds(tree, id).forEach(walkAll);
        };
        tree.roots.forEach(walkAll);
        return out;
    };

    const openDelete = (id) => {
        const u = tree.byId.get(id);
        if (!u) return;
        const parent = u.parent_id != null ? tree.byId.get(u.parent_id) : null;
        setDeleteFor({ id, name: u.name, parentName: parent?.name || null, sub: childIds(tree, id).length, people: (byUnit.get(id) || []).length });
    };

    const confirmDelete = () => {
        const d = deleteFor;
        setDeleteFor(null);
        if (!d) return;
        if (selectedId === d.id) setSelectedId(null);
        act(() => deleteOrgUnit(d.id), "Подразделение удалено");
        if (d.archived) setArchived((a) => a.filter((x) => x.id !== d.id));
    };

    // Чем закончилось перетаскивание карточки: зона, другая карточка или пустое место.
    finishDrag.current = (id, dx, dy, target, zone) => {
        if (zone === "delete") { openDelete(id); return; }
        if (zone === "top") { if (tree.byId.get(id)?.parent_id != null) moveUnit(id, null); return; }
        if (target != null && target !== tree.byId.get(id)?.parent_id && canMoveUnit(tree, id, target)) { moveUnit(id, target); return; }
        nudge(id, dx, dy);
    };

    const openDrawer = async (kind) => {
        setDrawer(kind);
        try {
            if (kind === "log") setLog(await getOrgChartLog());
            if (kind === "archive") setArchived(((await getOrgChart({ archived: true })).units || []).filter((u) => u.is_archived));
        } catch (e) {
            showAlert(e?.message || "Не удалось загрузить", "error");
        }
    };

    const selected = selectedId != null ? tree.byId.get(selectedId) : null;
    const menuUnit = menu ? tree.byId.get(menu.unitId) : null;
    const extraParentNames = (id) => links.filter((l) => l.unit_id === id).map((l) => tree.byId.get(l.parent_id)?.name).filter(Boolean);

    const listRows = [];
    const walk = (id, depth) => {
        if (hidden.has(id)) return;
        listRows.push({ id, depth });
        if (!collapsed.has(id)) childIds(tree, id).forEach((c) => walk(c, depth + 1));
    };
    if (prefs.view === "list") tree.roots.forEach((r) => walk(r, 0));

    const minX = layout.minX || 0;
    const minY = layout.minY || 0;
    const draggedHasParent = dragLive ? tree.byId.get(dragLive.id)?.parent_id != null : false;

    const menuItems = !menuUnit ? [] : [
        ...(canEdit ? [
            ["Добавить подотдел", () => setModal({ parentId: menuUnit.id })],
            ["Добавить вышестоящее", () => setModal({ parentId: menuUnit.parent_id ?? null, above: menuUnit.id })],
            ["Подчинить другому…", () => setMoveFor(menuUnit.id)],
            ...(menuUnit.parent_id != null ? [["На верхний уровень", () => moveUnit(menuUnit.id, null)]] : []),
        ] : []),
        ["Открыть", () => setSelectedId(menuUnit.id)],
        ...(canEdit ? [
            ["Левее", () => shift(menuUnit.id, -1)],
            ["Правее", () => shift(menuUnit.id, 1)],
            ...(offsets.has(menuUnit.id) ? [["Вернуть на автоматическое место", () => nudge(menuUnit.id, -offsets.get(menuUnit.id).dx, -offsets.get(menuUnit.id).dy)]] : []),
        ] : []),
        ["Скрыть у меня", () => hideUnit(menuUnit.id)],
        ...(canEdit ? [["Удалить…", () => openDelete(menuUnit.id)]] : []),
    ];

    return (
        <MainLayout contentClassName="h-full">
            <div className="flex flex-col h-[calc(100vh-88px)] min-h-[480px] -mx-2">
                <div className="flex flex-wrap items-center gap-2 px-2 pb-2">
                    <h1 className="text-xl font-bold text-slate-900 mr-1">Структура компании</h1>
                    {canEdit && (
                        <button type="button" onClick={() => setModal({ parentId: null })} className="px-3 py-1.5 rounded-lg bg-[#e0bb48] text-sm font-semibold">
                            + Добавить
                        </button>
                    )}
                    <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs">
                        {[["tree", "Дерево"], ["list", "Список"]].map(([k, l]) => (
                            <button key={k} type="button" onClick={() => updatePrefs({ view: k })}
                                className={`px-3 py-1.5 ${prefs.view === k ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`}>{l}</button>
                        ))}
                    </div>
                    {prefs.view !== "list" && (
                        <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs" role="group" aria-label="Раскладка">
                            {[["v", "↓ Сверху вниз"], ["h", "→ Слева направо"]].map(([k, l]) => (
                                <button key={k} type="button" onClick={() => { fitted.current = false; updatePrefs({ direction: k }); }} aria-pressed={direction === k}
                                    className={`px-3 py-1.5 ${direction === k ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`}>{l}</button>
                            ))}
                        </div>
                    )}
                    <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1 min-w-[220px] flex-1 max-w-md">
                        <span className="text-slate-400 text-sm" aria-hidden="true">⌕</span>
                        <input
                            value={query}
                            onChange={(e) => { setQuery(e.target.value); setHitIndex(0); }}
                            onKeyDown={(e) => { if (e.key === "Enter") gotoHit(); }}
                            placeholder="Отдел, сотрудник или должность"
                            className="flex-1 text-sm outline-none"
                            aria-label="Поиск по структуре"
                        />
                        {query && (
                            <>
                                <span className="text-[11px] text-slate-400 whitespace-nowrap" data-testid="org-hits">{hits.length ? `найдено: ${hits.length}` : "нет"}</span>
                                <button type="button" onClick={gotoHit} disabled={!hits.length} className="text-xs text-slate-500 px-1" aria-label="Следующее совпадение">↓</button>
                            </>
                        )}
                    </div>
                    <button type="button" onClick={() => (drawer === "fields" ? setDrawer(null) : setDrawer("fields"))}
                        className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs" aria-label="Какие данные показывать">
                        Данные на карточках ▾
                    </button>
                    <button type="button" onClick={() => foldAll(false)} className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-xs">Развернуть всё</button>
                    <button type="button" onClick={() => foldAll(true)} className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-xs">Свернуть всё</button>
                    {hidden.size > 0 && (
                        <button type="button" onClick={() => setDrawer(drawer === "hidden" ? null : "hidden")}
                            className="px-2 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-xs font-semibold" data-testid="org-hidden-button">
                            Скрыто: {hidden.size}
                        </button>
                    )}
                    {canEdit && (
                        <>
                            {offsets.size > 0 && prefs.view !== "list" && (
                                <button type="button" onClick={() => act(() => resetOrgPositions(direction), "Расположение сброшено")}
                                    className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-xs" title="Вернуть все карточки на автоматические места">
                                    Сбросить расположение
                                </button>
                            )}
                            <button type="button" onClick={() => openDrawer("log")} className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-xs">Журнал</button>
                            <button type="button" onClick={() => openDrawer("archive")} className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-xs">Архив</button>
                        </>
                    )}
                </div>

                {canEdit && units.length > 0 && prefs.view === "tree" && (
                    <p className="px-2 pb-2 text-xs text-slate-500" data-testid="org-hint">
                        Тащите карточку мышью: на другое подразделение — подчинить ему, на пустое место — просто сдвинуть (ветка едет вместе, расположение видят все).
                        «+» — новый подотдел, «⋯» — подчинить другому, добавить вышестоящее, скрыть, удалить. Дополнительное подчинение — в панели подразделения (пунктир).
                    </p>
                )}
                {error && <div className="mx-2 mb-2 rounded-lg bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}

                <div className="relative flex flex-1 min-h-0 rounded-2xl border border-slate-200 bg-slate-50 overflow-hidden">
                    <div className="relative flex-1 min-w-0">
                        {chart && !units.length ? (
                            <div className="h-full flex flex-col items-center justify-center gap-3 text-center p-6">
                                <p className="text-slate-600">Структура пока пустая.</p>
                                {canEdit ? (
                                    <div className="flex gap-2">
                                        <button type="button" onClick={() => setModal({ parentId: null })} className="px-4 py-2 rounded-lg bg-[#e0bb48] text-sm font-semibold">Добавить подразделение</button>
                                        <button type="button" onClick={() => act(importOrgDepartments, "Подразделения созданы из текущих отделов")}
                                            className="px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm">Создать из текущих отделов</button>
                                    </div>
                                ) : <p className="text-xs text-slate-400">Её заполнит тот, у кого есть право менять структуру.</p>}
                            </div>
                        ) : prefs.view === "list" ? (
                            <div className="h-full overflow-auto p-2">
                                <table className="w-full text-sm bg-white rounded-xl overflow-hidden">
                                    <thead className="text-xs text-slate-500 bg-slate-100">
                                        <tr><th className="text-left px-3 py-2">Подразделение</th><th className="text-left px-3 py-2">Руководители</th><th className="text-right px-3 py-2">Сотрудников</th></tr>
                                    </thead>
                                    <tbody>
                                        {listRows.map(({ id, depth }) => {
                                            const u = tree.byId.get(id);
                                            const kids = childIds(tree, id).length;
                                            const extra = extraParentNames(id);
                                            return (
                                                <tr key={id} className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${selectedId === id ? "bg-sky-50" : hitSet.has(id) ? "bg-amber-50" : ""}`}
                                                    onClick={() => setSelectedId(id)} data-testid="org-row">
                                                    <td className="px-3 py-1.5" style={{ paddingLeft: 12 + depth * 20 }}>
                                                        {kids > 0 ? (
                                                            <button type="button" onClick={(e) => { e.stopPropagation(); toggle(id); }} className="w-4 text-slate-400" aria-label={`${collapsed.has(id) ? "Развернуть" : "Свернуть"} ${u.name}`}>
                                                                {collapsed.has(id) ? "▸" : "▾"}
                                                            </button>
                                                        ) : <span className="inline-block w-4" />}
                                                        <span className="font-semibold text-slate-800">{u.name}</span>
                                                        {myUnits.has(id) && <span className="ml-2 text-[10px] bg-sky-500 text-white rounded-full px-1.5">ваш отдел</span>}
                                                        {extra.length > 0 && <span className="ml-2 text-[11px] text-violet-700">⇢ также: {extra.join(", ")}</span>}
                                                    </td>
                                                    <td className="px-3 py-1.5 text-slate-600">
                                                        {leadersOf(byUnit, id).map((m) => employeesById.get(m.employee_id)?.full_name).filter(Boolean).join(", ") || <span className="text-slate-300">—</span>}
                                                    </td>
                                                    <td className="px-3 py-1.5 text-right tabular-nums">{peopleCount(tree, byUnit, id)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div
                                ref={viewportRef}
                                className="absolute inset-0 overflow-hidden cursor-grab active:cursor-grabbing"
                                data-pan="1"
                                onMouseDown={(e) => {
                                    if (e.button !== 0 || !e.target.closest?.("[data-pan]") || e.target.closest?.("[data-testid='org-card']")) return;
                                    panning.current = { sx: e.clientX, sy: e.clientY, px: pan.x, py: pan.y };
                                    setMenu(null);
                                }}
                                data-testid="org-canvas"
                            >
                                <div className="absolute top-0 left-0" data-pan="1" style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: "0 0", width: layout.width, height: layout.height }}>
                                    <svg className="absolute overflow-visible pointer-events-none" style={{ left: minX, top: minY }} width={layout.width - minX} height={layout.height - minY}>
                                        <g transform={`translate(${-minX},${-minY})`}>
                                            {layout.edges.map(({ from, to }) => {
                                                const a = nodeById.get(from);
                                                const b = nodeById.get(to);
                                                if (!a || !b) return null;
                                                return <path key={`${from}-${to}`} d={edgePath(a, b, direction)} fill="none" stroke="#94a3b8" strokeWidth="1.5" />;
                                            })}
                                            {links.map((lk) => {
                                                const a = nodeById.get(lk.parent_id);
                                                const b = nodeById.get(lk.unit_id);
                                                if (!a || !b) return null;
                                                return <path key={`link-${lk.id}`} d={linkPath(a, b, direction)} fill="none" stroke="#8b5cf6" strokeWidth="1.5" strokeDasharray="6 4" data-testid="org-link" />;
                                            })}
                                        </g>
                                    </svg>
                                    {layout.nodes.map((n) => {
                                        const u = tree.byId.get(n.id);
                                        const kids = childIds(tree, n.id).filter((k) => !hidden.has(k)).length;
                                        return (
                                            <OrgCard
                                                key={n.id}
                                                node={n}
                                                unit={u}
                                                leaders={leadersOf(byUnit, n.id)}
                                                staff={staffOf(byUnit, n.id)}
                                                employeesById={employeesById}
                                                fieldLines={fieldLines}
                                                fields={fields}
                                                roles={roles}
                                                people={peopleCount(tree, byUnit, n.id)}
                                                kids={kids}
                                                collapsed={collapsed.has(n.id)}
                                                mine={myUnits.has(n.id)}
                                                selected={selectedId === n.id}
                                                matched={hitSet.has(n.id)}
                                                canEdit={canEdit}
                                                dropOk={dropId === n.id}
                                                dragging={dragLive != null && subtreeIds(tree, dragLive.id).includes(n.id)}
                                                extraParents={extraParentNames(n.id)}
                                                showStaff={showStaff}
                                                direction={direction}
                                                onSelect={guardClick(() => setSelectedId(n.id))}
                                                onToggle={guardClick(() => toggle(n.id))}
                                                onAddChild={() => setModal({ parentId: n.id })}
                                                onMenu={(e) => setMenu({ unitId: n.id, x: e.clientX, y: e.clientY })}
                                                onMouseDown={(e) => startCardDrag(e, n.id)}
                                            />
                                        );
                                    })}
                                </div>
                                {dragLive != null && (
                                    <>
                                        {draggedHasParent && (
                                            <div data-dropzone="top"
                                                className={`absolute top-3 left-1/2 -translate-x-1/2 z-20 rounded-xl border-2 border-dashed px-5 py-2 text-sm font-semibold ${dropZone === "top" ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-300 bg-white/90 text-slate-500"}`}
                                                data-testid="org-drop-top">
                                                Отпустите здесь — на верхний уровень
                                            </div>
                                        )}
                                        <div data-dropzone="delete"
                                            className={`absolute right-3 bottom-3 z-20 rounded-xl border-2 border-dashed px-5 py-3 text-sm font-semibold ${dropZone === "delete" ? "border-red-500 bg-red-50 text-red-700" : "border-red-200 bg-white/90 text-red-400"}`}
                                            data-testid="org-drop-delete">
                                            🗑 Удалить
                                        </div>
                                    </>
                                )}
                                <div className="absolute left-3 bottom-3 flex items-center gap-1 bg-white border border-slate-200 rounded-xl shadow-sm px-2 py-1 text-xs" onMouseDown={(e) => e.stopPropagation()}>
                                    <button type="button" onClick={findMe} className="px-2 py-1 rounded-md hover:bg-slate-100 font-semibold">Найти меня</button>
                                    <span className="w-px h-4 bg-slate-200" />
                                    <button type="button" onClick={() => setZoom((z) => Math.max(ZOOM_MIN, Number((z - 0.1).toFixed(2))))} className="px-2 py-1 rounded-md hover:bg-slate-100" aria-label="Уменьшить">−</button>
                                    <span className="w-10 text-center tabular-nums" data-testid="org-zoom">{Math.round(zoom * 100)}%</span>
                                    <button type="button" onClick={() => setZoom((z) => Math.min(ZOOM_MAX, Number((z + 0.1).toFixed(2))))} className="px-2 py-1 rounded-md hover:bg-slate-100" aria-label="Увеличить">+</button>
                                    <button type="button" onClick={fitAll} className="px-2 py-1 rounded-md hover:bg-slate-100" title="Показать всё" aria-label="Показать всё">⤢</button>
                                </div>
                            </div>
                        )}

                        {drawer === "fields" && (
                            <Drawer title="Данные на карточках" onClose={() => setDrawer(null)}>
                                <p className="text-xs text-slate-500 mb-2">Только для вас. Что недоступно по правам — не показывается.</p>
                                <label className="flex items-center gap-2 py-1 font-semibold">
                                    <input type="checkbox" checked={showStaff} onChange={(e) => updatePrefs({ showStaff: e.target.checked })} aria-label="Список сотрудников на карточках" />
                                    Список сотрудников на карточках
                                </label>
                                <div className="border-t border-slate-100 my-1" />
                                {(chart?.fields || []).map((f) => (
                                    <label key={f.key} className={`flex items-center gap-2 py-1 ${f.allowed ? "" : "opacity-50"}`}>
                                        <input
                                            type="checkbox"
                                            disabled={!f.allowed}
                                            checked={(prefs.fields || []).includes(f.key) && f.allowed}
                                            onChange={(e) => updatePrefs({
                                                fields: e.target.checked ? [...(prefs.fields || []).filter((k) => k !== f.key), f.key] : (prefs.fields || []).filter((k) => k !== f.key),
                                            })}
                                            aria-label={f.label}
                                        />
                                        {f.label}{!f.allowed && <span className="text-[11px] text-slate-400">нет доступа</span>}
                                    </label>
                                ))}
                            </Drawer>
                        )}
                        {drawer === "hidden" && (
                            <Drawer title="Скрытые у меня" onClose={() => setDrawer(null)}>
                                <p className="text-xs text-slate-500 mb-2">Скрыты только для вас — у остальных отображаются.</p>
                                {[...hidden].map((id) => (
                                    <div key={id} className="flex items-center justify-between py-1.5 border-b border-slate-100">
                                        <span>{tree.byId.get(id)?.name}</span>
                                        <button type="button" onClick={() => updatePrefs({ hidden: [...hidden].filter((h) => h !== id) })} className="text-xs text-sky-700 hover:underline">Показать</button>
                                    </div>
                                ))}
                                {hidden.size > 0 && (
                                    <button type="button" onClick={() => { updatePrefs({ hidden: [] }); setDrawer(null); }} className="mt-2 text-xs font-semibold text-sky-700 hover:underline">Показать все</button>
                                )}
                            </Drawer>
                        )}
                        {drawer === "log" && (
                            <Drawer title="Журнал изменений" onClose={() => setDrawer(null)}>
                                {log.length ? log.map((r) => (
                                    <div key={r.id} className="py-1.5 border-b border-slate-100">
                                        <div className="text-[11px] text-slate-400">{r.at ? new Date(r.at).toLocaleString("ru-RU") : ""} · {r.actor_name || "—"}</div>
                                        <div className="text-slate-700">{r.text}</div>
                                    </div>
                                )) : <p className="text-slate-400">Изменений пока нет</p>}
                            </Drawer>
                        )}
                        {drawer === "archive" && (
                            <Drawer title="Архив подразделений" onClose={() => setDrawer(null)}>
                                {archived.length ? archived.map((u) => (
                                    <div key={u.id} className="flex items-center justify-between py-1.5 border-b border-slate-100">
                                        <span>{u.name}</span>
                                        <span className="flex gap-3">
                                            <button type="button" onClick={async () => { await act(() => restoreOrgUnit(u.id), "Восстановлено"); setArchived((a) => a.filter((x) => x.id !== u.id)); }}
                                                className="text-xs text-sky-700 hover:underline">Восстановить</button>
                                            <button type="button" onClick={() => setDeleteFor({ id: u.id, name: u.name, archived: true, sub: 0, people: 0 })}
                                                className="text-xs text-red-600 hover:underline">Удалить</button>
                                        </span>
                                    </div>
                                )) : <p className="text-slate-400">Архив пуст</p>}
                            </Drawer>
                        )}
                    </div>

                    {selected && (
                        <OrgUnitPanel
                            unit={selected}
                            tree={tree}
                            byUnit={byUnit}
                            employeesById={employeesById}
                            employees={chart?.employees || []}
                            roles={roles}
                            fields={fields}
                            canEdit={canEdit}
                            units={units}
                            departments={chart?.departments || []}
                            onClose={() => setSelectedId(null)}
                            onSelectUnit={reveal}
                            onUpdateUnit={(id, data) => act(() => updateOrgUnit(id, data))}
                            parentOptions={parentOptions(selected.id)}
                            onMoveUnit={(parentId) => moveUnit(selected.id, parentId)}
                            onDeleteUnit={() => openDelete(selected.id)}
                            extraLinks={links.filter((l) => l.unit_id === selected.id).map((l) => ({ id: l.id, parent_id: l.parent_id, name: tree.byId.get(l.parent_id)?.name }))}
                            linkOptions={parentOptions(selected.id).filter((o) => o.id !== selected.parent_id && !links.some((l) => l.unit_id === selected.id && l.parent_id === o.id))}
                            onAddLink={(parentId) => act(() => addOrgLink(selected.id, parentId), "Добавлено дополнительное подчинение")}
                            onRemoveLink={(linkId) => act(() => removeOrgLink(linkId), "Дополнительное подчинение убрано")}
                            onAddMember={(id, data) => act(() => addOrgMember(id, data))}
                            onUpdateMember={(id, data) => act(() => updateOrgMember(id, data))}
                            onRemoveMember={(m) => {
                                const name = employeesById.get(m.employee_id)?.full_name || "сотрудника";
                                if (window.confirm(`Убрать ${name} из «${selected.name}»?`)) act(() => removeOrgMember(m.id));
                            }}
                        />
                    )}
                </div>
            </div>

            {menu && menuUnit && (
                <div className="fixed inset-0 z-40" onClick={() => setMenu(null)} role="presentation">
                    <div className="absolute bg-white border border-slate-200 rounded-xl shadow-lg py-1 text-sm min-w-[220px]"
                        style={{ left: Math.min(menu.x, window.innerWidth - 240), top: Math.min(menu.y, window.innerHeight - 340) }}
                        role="menu" aria-label={`Действия: ${menuUnit.name}`} onClick={(e) => e.stopPropagation()}>
                        {menuItems.map(([label, fn]) => (
                            <button key={label} type="button" role="menuitem" onClick={() => { setMenu(null); fn(); }}
                                className={`block w-full text-left px-3 py-1.5 hover:bg-slate-50 ${label === "Удалить…" ? "text-red-600 border-t border-slate-100 mt-1" : ""}`}>{label}</button>
                        ))}
                    </div>
                </div>
            )}

            {modal && (
                <UnitModal
                    units={modal.above ? units.filter((u) => u.id !== modal.above && canMoveUnit(tree, modal.above, u.id)) : units}
                    parentId={modal.parentId}
                    title={modal.above ? `Вышестоящее для «${tree.byId.get(modal.above)?.name || ""}»` : "Новое подразделение"}
                    onClose={() => setModal(null)}
                    onCreate={(data) => {
                        const above = modal.above;
                        setModal(null);
                        act(async () => {
                            const created = await createOrgUnit(data);
                            if (above && created?.id) await updateOrgUnit(above, { parent_id: created.id });
                        }, "Подразделение создано");
                    }}
                />
            )}
            {deleteFor && <DeleteModal target={deleteFor} onClose={() => setDeleteFor(null)} onConfirm={confirmDelete} />}
            {moveFor != null && tree.byId.get(moveFor) && (
                <MoveModal
                    unit={tree.byId.get(moveFor)}
                    options={parentOptions(moveFor).filter((o) => o.id !== tree.byId.get(moveFor).parent_id)}
                    onClose={() => setMoveFor(null)}
                    onMove={(parentId) => { const id = moveFor; setMoveFor(null); moveUnit(id, parentId); }}
                />
            )}
        </MainLayout>
    );
}
